/**
 * insurance-premium-shape.ts — which of the two insurance instruments a year is.
 *
 * A year in which Primelane paid the premium and recovers it is an INSURANCE PREMIUM ADVANCE.
 * A year in which the DRIVER paid the whole premium themselves is an INSURANCE PREMIUM PAYMENT
 * RECORD [D-B45]: Primelane advanced nothing, no interest arises, and nothing is owed for it.
 * Both are carried by the same rows — `insurance_loans` plus its deposits — so the only thing
 * that separates them is the SHAPE OF THE MONEY, and that shape is read here once for every
 * surface that has to name the instrument.
 *
 * ── THE TWO LIMBS ARE THE SERVER'S, VERBATIM ─────────────────────────────────
 * `agreement_create` derives the instrument with
 *
 *     SUM(deposits) >= amount - 0.005  AND  NOT EXISTS (instalments)
 *
 * and `agreement_sign` re-asserts the same two limbs before it seals a page. This predicate is
 * that one, reading the same two figures off `get_contract_insights`' loan payload
 * (`deposit_total` is `SUM(insurance_loan_deposits)`, `instalments` is NULL when the loan has
 * no instalment row). A LIMB THE SERVER DOES NOT HAVE — a rate test, a test on the repayments
 * — would be a client that disagrees with the server about which document the driver is
 * holding, which is the one failure this module exists to prevent. The server derivation is
 * the truth; this chooses the words printed over it.
 *
 * ── WHY `amount > 0` IS PART OF THE VERBATIM AND NOT A THIRD LIMB ─────────────
 * The server's deposit limb is `COALESCE(SUM(deposits) >= il.amount - 0.005, false)`, and a
 * loan with NO deposit rows sums to SQL NULL: the comparison is NULL, COALESCE makes it false,
 * and a premium of zero or less therefore answers ADVANCE server-side. This payload cannot
 * reproduce that, because `deposit_total` COALESCEs to 0 before it reaches the app — and
 * `0 >= 0 - 0.005` is true, so the unguarded predicate answered RECORD on exactly the shape
 * the server answers ADVANCE on. The `amount > 0` guard below is what makes the two limbs
 * verbatim rather than what breaks them. No live loan reaches it (both admin write paths
 * refuse a non-positive premium), so it is robustness and not a repair.
 *
 * THE TOLERANCE IS NOT DECORATION. `transactions.amount` is `double precision` and a premium
 * settled in two transfers is a sum of floats; 0.005 is half a pesewa, below the smallest
 * figure any of these surfaces prints, so a driver who has paid the whole premium is never told
 * they are a fraction of a pesewa short of it.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DECIDE ─────────────────────────────────────
 * A PARTLY deposited advance is an advance: the driver contributed, Primelane advanced the
 * rest, and interest runs on the difference. Nothing here changes for it — the partial-deposit
 * arithmetic on the Insurance tab is a separate question with its own divergence between this
 * app and the admin, and it is not this branch's to answer.
 */

/** The insurance loan as `get_contract_insights` publishes it, to the depth this reads. */
export interface PremiumLoanShape {
    amount?: number | null;
    deposit_total?: number | null;
    /** `json_agg` over `insurance_loan_instalments` — NULL, never `[]`, when there are none. */
    instalments?: readonly unknown[] | null;
    /** Deposits and repayments interleaved; `is_deposit` separates them. */
    repayments?: readonly { paid_at?: string | null; is_deposit?: boolean }[] | null;
}

/** Half a pesewa — see the docblock. The same constant the server's selector carries. */
const FULL_PAYMENT_TOLERANCE = 0.005;

/**
 * Whether the driver paid this premium in full — the Record's shape rather than the Advance's.
 *
 * Fails closed on a payload that cannot answer: a missing amount or a missing deposit total is
 * not evidence of a full payment, and the Advance wording is the one that is true of every
 * insurance year this app has shown until now.
 */
export function premiumPaidInFull(loan: PremiumLoanShape | null | undefined): boolean {
    if (!loan) return false;

    const amount = loan.amount;
    const deposit = loan.deposit_total;
    if (typeof amount !== 'number' || !Number.isFinite(amount)) return false;
    if (typeof deposit !== 'number' || !Number.isFinite(deposit)) return false;

    // A premium of zero or less is the one shape where `deposit_total`'s COALESCE-to-0 and the
    // server's NULL SUM part company — see the docblock. The server answers ADVANCE; so does this.
    if (!(amount > 0)) return false;

    // An instalment row is a promise to repay something, which is a thing no Record contains.
    if ((loan.instalments?.length ?? 0) > 0) return false;

    return deposit >= amount - FULL_PAYMENT_TOLERANCE;
}

/**
 * The day the premium was paid: the LATEST deposit transaction on the loan.
 *
 * ── LATEST, AND EXPLICITLY SO ────────────────────────────────────────────────
 * A premium can be settled in more than one transfer (live loan 294 was paid in two), and the
 * date a driver is asked to confirm is the day the payment was COMPLETED — an earlier
 * instalment of it would say the premium was settled before it was. `get_contract_insights`
 * already returns this list `transaction_at DESC`, so the answer is normally its first deposit
 * row, but the maximum is taken here rather than trusting a server-side ORDER BY to survive a
 * future edit to the query — the same reason `scheduleWeeklyInstalment` takes its own.
 *
 * Deposits only. The repayments list interleaves both kinds, and a repayment against a Record
 * is a row that should not exist; if one ever did, it would not be the day the premium was paid.
 */
export function latestPremiumPaymentAt(loan: PremiumLoanShape | null | undefined): string | null {
    let latest: string | null = null;
    let latestAt = Number.NEGATIVE_INFINITY;

    for (const row of loan?.repayments ?? []) {
        if (!row || row.is_deposit !== true) continue;
        const paidAt = row.paid_at;
        if (typeof paidAt !== 'string') continue;
        const at = Date.parse(paidAt);
        if (Number.isNaN(at)) continue;
        if (at > latestAt) {
            latestAt = at;
            latest = paidAt;
        }
    }

    return latest;
}
